import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  createListingLayoutLink,
  deleteListingLayoutLink,
  resolveLayoutUuidForPlan,
} from './analyticsListingApi';
import {
  buildLinksByCustomerRef,
  getReplaceCandidateForListingRef,
} from './listingLinkPreflight';
import { CLIENT_COPY_LISTING_PHRASE } from './signupFloorPlanOrigin';
import { ANALYTICS_RED, analyticsGreenAlpha } from './analyticsColors';
import {
  getListingInputModalDialogProps,
  LISTING_INPUT_MODAL_CONTENT_SX,
} from './listingInputModalStyles';
import { ListingModalExpandRow, ListingModalTitleBar } from './listingModalUi';
import ListingIdPicker from './ListingIdPicker';
import ListingStatusFields from './ListingStatusFields';
import { parseDateInput, requiresOffMarketDate } from './listingMarketDates';

/**
 * Attach one pre-selected floor plan (from library) to a listing.
 */
const AttachListingModal = ({
  open,
  onClose,
  partner,
  selectedPlan = null,
  initialListingId = '',
  existingLinks = [],
  planByCustomerRef = null,
  mlsSyncEnabled = false,
  onSuccess,
}) => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const dialogProps = getListingInputModalDialogProps(fullScreenDialog);

  const defaultListingAddress = useMemo(() => {
    const fromProp = String(initialListingId || '').trim();
    if (fromProp) return fromProp;
    return String(selectedPlan?.name || '').trim();
  }, [initialListingId, selectedPlan?.name]);

  const [listingAddress, setListingAddress] = useState(defaultListingAddress);
  const [matchedListing, setMatchedListing] = useState(null);
  const [customPropertyStatus, setCustomPropertyStatus] = useState('');
  const [onMarketDate, setOnMarketDate] = useState('');
  const [offMarketDate, setOffMarketDate] = useState('');
  const [backfillEvents, setBackfillEvents] = useState(true);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [replaceCandidate, setReplaceCandidate] = useState(null);

  useEffect(() => {
    if (!open) return;
    setListingAddress(defaultListingAddress);
    setMatchedListing(null);
    setCustomPropertyStatus('');
    setOnMarketDate('');
    setOffMarketDate('');
    setBackfillEvents(true);
    setAdvancedExpanded(false);
    setError(null);
    setReplaceCandidate(null);
  }, [open, defaultListingAddress, selectedPlan?._id]);

  const linksByRef = useMemo(
    () => buildLinksByCustomerRef(existingLinks),
    [existingLinks]
  );

  const runCreate = async ({ ref, deletePriorLinkId = null }) => {
    setSaving(true);
    setError(null);
    try {
      if (deletePriorLinkId) {
        await deleteListingLayoutLink(deletePriorLinkId, partner);
      }
      const layoutUuid = await resolveLayoutUuidForPlan(partner, selectedPlan);
      // A ref picked from search is an exact feed identifier, so the listing
      // tracks automatically and manual status would only shadow it.
      const autoTracked = Boolean(matchedListing) && matchedListing.listingRef === ref;
      const manualPaused = mlsSyncEnabled || autoTracked;
      const onIso =
        !manualPaused && onMarketDate.trim() ? parseDateInput(onMarketDate) : null;
      const offIso =
        !manualPaused &&
        requiresOffMarketDate(customPropertyStatus) &&
        offMarketDate.trim()
          ? parseDateInput(offMarketDate)
          : null;
      const result = await createListingLayoutLink({
        customer_ref: ref,
        layoutUuid,
        partner,
        backfillEvents,
        customPropertyStatus: manualPaused ? null : customPropertyStatus || null,
        onMarketDate: onIso,
        offMarketDate: offIso,
        autoSyncListing: autoTracked,
      });
      onSuccess?.(result);
      onClose();
    } catch (e) {
      const msg = e?.message || 'Something went wrong. Try again.';
      if (/409|already linked/i.test(msg)) {
        setError('This floor plan is already linked to that listing ID.');
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const ref = listingAddress.trim();
    if (!ref) {
      setError('Enter a listing ID.');
      return;
    }
    if (!selectedPlan) {
      setError('No floor plan selected.');
      return;
    }
    if (selectedPlan.isFork) {
      setError(`Link the original agent upload, not a client copy. ${CLIENT_COPY_LISTING_PHRASE}`);
      return;
    }
    const autoTracked = Boolean(matchedListing) && matchedListing.listingRef === ref;
    if (!mlsSyncEnabled && !autoTracked) {
      if (onMarketDate.trim() && parseDateInput(onMarketDate) === undefined) {
        setError('On-market date is not valid.');
        return;
      }
      if (requiresOffMarketDate(customPropertyStatus) && !offMarketDate.trim()) {
        setError('Enter the off-market date for under contract or sold.');
        return;
      }
      if (
        requiresOffMarketDate(customPropertyStatus) &&
        offMarketDate.trim() &&
        parseDateInput(offMarketDate) === undefined
      ) {
        setError('Off-market date is not valid.');
        return;
      }
    }

    const replace = getReplaceCandidateForListingRef({
      listingRef: ref,
      layoutUuid: selectedPlan.uuid || null,
      linksByRef,
      planByCustomerRef,
    });
    if (replace) {
      setReplaceCandidate(replace);
      return;
    }

    await runCreate({ ref });
  };

  const handleConfirmReplace = async () => {
    if (!replaceCandidate) return;
    const { ref, priorLinkId } = replaceCandidate;
    setReplaceCandidate(null);
    await runCreate({ ref, deletePriorLinkId: priorLinkId });
  };

  if (replaceCandidate) {
    return (
      <Dialog
        open={open}
        onClose={saving ? undefined : () => setReplaceCandidate(null)}
        {...dialogProps}
      >
        <ListingModalTitleBar
          title="Replace existing link?"
          onClose={() => setReplaceCandidate(null)}
          onSave={handleConfirmReplace}
          saving={saving}
          saveAriaLabel="Replace link"
        />
        <DialogContent sx={LISTING_INPUT_MODAL_CONTENT_SX}>
          <Stack spacing={2}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(0,0,0,0.65)', lineHeight: 1.5 }}>
              {replaceCandidate.ref} is already linked to
              {replaceCandidate.priorPlanName ? ' ' : ' another floor plan.'}
              {replaceCandidate.priorPlanName && (
                <Typography component="span" sx={{ fontWeight: 600 }}>
                  {replaceCandidate.priorPlanName}
                </Typography>
              )}
              {replaceCandidate.priorPlanName ? '.' : ''} Replacing will unlink the existing floor plan
              and link {selectedPlan?.name || 'this floor plan'} instead.
            </Typography>
            <Stack spacing={1}>
              {replaceCandidate.priorPlanName && (
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(0,0,0,0.03)' }}
                >
                  <Avatar
                    variant="rounded"
                    src={replaceCandidate.priorPlanThumb || undefined}
                    sx={{ width: 36, height: 36, borderRadius: '8px', flexShrink: 0 }}
                  >
                    {replaceCandidate.priorPlanName?.[0] || 'F'}
                  </Avatar>
                  <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.6)' }} noWrap>
                    Currently linked: {replaceCandidate.priorPlanName}
                  </Typography>
                </Stack>
              )}
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{ p: 1, borderRadius: '10px', bgcolor: analyticsGreenAlpha(0.06) }}
              >
                <Avatar
                  variant="rounded"
                  src={selectedPlan?.thumbnail || undefined}
                  sx={{ width: 36, height: 36, borderRadius: '8px', flexShrink: 0 }}
                >
                  {selectedPlan?.name?.[0] || 'F'}
                </Avatar>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.78)' }} noWrap>
                  Link instead: {selectedPlan?.name || 'this floor plan'}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} {...dialogProps}>
      <ListingModalTitleBar
        title="Link to listing"
        subtitle={selectedPlan?.name || null}
        onClose={onClose}
        onSave={handleSave}
        saving={saving}
        saveDisabled={!selectedPlan}
        saveAriaLabel="Link listing"
      />
      <DialogContent sx={LISTING_INPUT_MODAL_CONTENT_SX}>
        <Stack spacing={{ xs: 2, sm: 2.25 }}>
          {selectedPlan?.isFork && selectedPlan.copiedFromName ? (
            <Typography sx={{ fontSize: '0.78rem', color: '#b45309', lineHeight: 1.45 }}>
              This is a client copy. Link the original upload instead — {CLIENT_COPY_LISTING_PHRASE}
            </Typography>
          ) : null}

          <ListingIdPicker
            partner={partner}
            value={listingAddress}
            onChange={setListingAddress}
            onSelect={(listing) => {
              setMatchedListing(listing);
              if (listing) {
                setCustomPropertyStatus('');
                setOnMarketDate('');
                setOffMarketDate('');
              }
            }}
            selectedListing={matchedListing}
            autoFocus
            disabled={saving}
          />

          {matchedListing ? null : (
            <ListingStatusFields
              compact
              theme={theme}
              mlsSyncEnabled={mlsSyncEnabled}
              customPropertyStatus={customPropertyStatus}
              onCustomPropertyStatusChange={setCustomPropertyStatus}
              onMarketDate={onMarketDate}
              onOffMarketDate={offMarketDate}
              onOnMarketDateChange={setOnMarketDate}
              onOffMarketDateChange={setOffMarketDate}
              disabled={saving}
            />
          )}

          <ListingModalExpandRow
            label="More options"
            open={advancedExpanded}
            onToggle={() => setAdvancedExpanded((prev) => !prev)}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={backfillEvents}
                  onChange={(e) => setBackfillEvents(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.55)' }}>
                  Include past activity for this floor plan in listing totals
                </Typography>
              }
            />
          </ListingModalExpandRow>

          {error ? (
            <Typography sx={{ fontSize: '0.78rem', color: ANALYTICS_RED, px: 0.25 }}>
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default AttachListingModal;
